/**
 * SPA-side template capability resolver.
 *
 * Source of truth for capabilities lives in each plugin's templates on the
 * server side (the `src/templates` directory under each plugin package).
 * Until we expose that registry via `GET /api/v1/applications/templates`, the SPA mirrors the
 * known SaaS template ids here so it can gate UI on `capabilities.scripts`
 * without an extra round-trip.
 *
 * Adding a new SaaS template means adding its id to `SAAS_TEMPLATE_IDS`.
 * Anything not in this list is assumed to be infra-flavoured — same as the
 * server-side `DEFAULT_CAPABILITIES` (scripts: true, operations: false,
 * repoDiscovery: true).
 */

/** Template ids known to be SaaS — scripts/repo discovery disabled. */
const SAAS_TEMPLATE_IDS = new Set(['microsoft-365', 'google-workspace']);

export interface ResolvedCapabilities {
  scripts: boolean;
  operations: boolean;
  repoDiscovery: boolean;
}

const INFRA_DEFAULT: ResolvedCapabilities = {
  scripts: true,
  operations: false,
  repoDiscovery: true,
};

const SAAS_DEFAULT: ResolvedCapabilities = {
  scripts: false,
  operations: true,
  repoDiscovery: false,
};

/**
 * Resolve capabilities for an application by its `templateId`. Null/undefined
 * templateId ⇒ infra defaults (the no-template-picked path). Unknown id ⇒
 * infra defaults too — we err on the side of not hiding things by accident.
 */
export function resolveCapabilities(templateId: string | null | undefined): ResolvedCapabilities {
  if (!templateId) return INFRA_DEFAULT;
  if (SAAS_TEMPLATE_IDS.has(templateId)) return SAAS_DEFAULT;
  return INFRA_DEFAULT;
}
